import { useState, useCallback, useEffect, useRef } from 'react'
import { QuickSearchResponseRecord, SearchFacet } from '@/search/types'
import TypesenseClient from '@/search/Typesense'

// Update this to change the quick search provider
const quickSearchProvider = new TypesenseClient()

const collection = 'products'

// Helper to translate sort option to sort string
const translateSort = (sort?: QuickSearchSortOption): string | undefined => {
  if (!sort) return undefined
  return quickSearchProvider.sortByTranslate(sort)
}

// Helper to create stable string from array for dependency comparison
const arrayToKey = (arr?: string[]): string => {
  if (!arr || arr.length === 0) return ''
  return arr.slice().sort().join(',')
}

export function useQuickSearch({
  query,
  sort,
  filters,
  productTypes,
  pagination,
  onError,
  includeTypes,
  omitSortBy,
  includeFields = [
    'title',
    'creators',
    'creator_names',
    'creator_slugs',
    'thumbnail_url',
    'product_url',
    'object_id',
    'discount_usd',
    'price_usd',
    'discount_gbp',
    'price_gbp',
    'categories',
    'product_type',
    'product_type_description',
    'in_stock',
    'type',
    'options',
  ],
}: UseQuickSearchProps) {
  const [results, setResults] = useState<QuickSearchResponseRecord[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [facets, setFacets] = useState<SearchFacet[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const currentQueryRef = useRef<string>('')

  const clearResults = useCallback(() => {
    setResults([])
    setFacets([])
    setError(null)
  }, [])

  // Handle empty query separately to avoid state update loops
  useEffect(() => {
    if (!query || query.trim().length === 0) {
      if (currentQueryRef.current !== '') {
        currentQueryRef.current = ''
        // Use setTimeout to defer state updates and avoid synchronous setState in effect
        setTimeout(() => {
          setResults([])
          setFacets([])
          setTotalCount(0)
          setIsLoading(false)
          setError(null)
        }, 0)
      }
      return
    }
  }, [query])

  // Handle search queries
  // Extract stable keys for dependencies
  const includeTypesKey = arrayToKey(includeTypes)
  const includeFieldsKey = arrayToKey(includeFields)
  const filtersKey = arrayToKey(filters)
  const productTypesKey = arrayToKey(productTypes)
  const paginationPage = pagination?.page
  const paginationLimit = pagination?.limit

  useEffect(() => {
    if (!query || query.trim().length === 0) {
      return
    }

    // Update ref and mark as current query
    const searchQuery = query
    currentQueryRef.current = searchQuery

    let cancelled = false

    const performSearch = async () => {
      setIsLoading(true)
      setError(null)

      // Build custom filter for blog posts if includeTypes includes 'blog'
      let customFilter: string | undefined
      if (includeTypes && includeTypes.includes('blog')) {
        // Filter for blog posts: type:Post (as used in old website)
        // Blog posts are indexed with type:Post (and also product_type:article)
        customFilter = 'type:Post'
      }

      try {
        const searchResults = await quickSearchProvider.searchProducts({
          collection,
          query: searchQuery,
          includeFields,
          sortBy: translateSort(sort),
          omitSortBy,
          ...pagination,
          filterBy: filters,
          productTypes,
          customFilter,
        })

        // Only update if query hasn't changed and not cancelled
        if (!cancelled && searchQuery === currentQueryRef.current) {
          // Filter by type if includeTypes is specified
          let filteredHits = searchResults.hits || []
          if (includeTypes && includeTypes.length > 0) {
            filteredHits = filteredHits.filter(
              hit => hit.resultType && includeTypes.includes(hit.resultType)
            )
          }

          setResults(filteredHits)
          setTotalCount(searchResults.totalCount)
          setFacets(searchResults.facets || [])
          setIsLoading(false)
        }
      } catch (err) {
        // Only update if query hasn't changed and not cancelled
        if (!cancelled && searchQuery === currentQueryRef.current) {
          const error = err instanceof Error ? err : new Error('Search failed')
          setError(error)
          setIsLoading(false)
          onError?.(error)
        }
      }
    }

    performSearch()

    // Cleanup function to cancel in-flight requests
    return () => {
      cancelled = true
    }
    // Use primitive values for dependencies to avoid infinite loops
    // We use stable keys (includeTypesKey, includeFieldsKey, filtersKey) instead of
    // the objects/arrays themselves to prevent infinite re-renders when references change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    paginationPage,
    paginationLimit,
    sort,
    omitSortBy,
    includeTypesKey,
    includeFieldsKey,
    filtersKey,
    productTypesKey,
    onError,
  ])

  const searchProducts = useCallback(async () => {
    // This is kept for backwards compatibility but the effect handles the search
    if (query && query.trim().length > 0) {
      // Triggered by useEffect
    }
  }, [query])

  return {
    searchProducts,
    results,
    isLoading,
    error,
    sortOptions,
    filterOptions,
    totalCount,
    facets,
    clearResults,
  }
}

interface UseQuickSearchProps {
  query: string
  pagination?: {
    page: number
    limit: number
  }
  sort?: QuickSearchSortOption
  /** Omit Typesense sort_by so ranking comes from the collection preset (header autocomplete). */
  omitSortBy?: boolean
  filters?: QuickSearchFilterOption[]
  productTypes?: string[]
  onError?: (error: Error) => void
  includeFields?: string[]
  includeTypes?: ('product' | 'blog' | 'page')[]
}

const sortOptions = {
  RELEVANCE: 'Relevance',
  PRICE_ASC: 'Price: Low to High',
  PRICE_DESC: 'Price: High to Low',
  DATE_ADDED_ASC: 'Date Added: New to Old',
  DATE_ADDED_DESC: 'Date Added: Old to New',
}

export type QuickSearchSortOption = keyof typeof sortOptions

const filterOptions = {
  IN_STOCK: 'In Stock',
  ON_SALE: 'On Sale',
}

export type QuickSearchFilterOption = keyof typeof filterOptions
